import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// =============================================
// TYPES
// =============================================

export type EncryptionAlgorithm =
  | "AES-256-GCM"
  | "AES-256-CBC"
  | "AES-128-GCM"
  | "ChaCha20-Poly1305";

export type ComplianceMode =
  | "none"
  | "hipaa"
  | "soc2"
  | "pci_dss"
  | "gdpr"
  | "hipaa_soc2";

export type MasterKeyStatus =
  | "active"
  | "pending_rotation"
  | "rotated"
  | "revoked"
  | "compromised";

export type RecoveryCodeStatus = "active" | "used" | "expired" | "revoked";

export type EnforcementMode = "warn" | "block" | "strict";

export interface EncryptionPolicy {
  id: string;
  tenant_id: string | null;
  approved_algorithms: EncryptionAlgorithm[];
  default_algorithm: EncryptionAlgorithm;
  min_key_length_bits: number;
  max_key_length_bits: number;
  require_strong_entropy: boolean;
  rotation_period_days: number;
  rotation_warning_days: number;
  enforce_rotation: boolean;
  enforcement_mode: EnforcementMode;
  auto_rotate: boolean;
  compliance_mode: ComplianceMode;
  require_key_escrow: boolean;
  require_dual_control: boolean;
  require_audit_logging: boolean;
  allow_key_export: boolean;
  require_hsm: boolean;
  key_derivation_function: string;
  key_derivation_iterations: number;
  created_at: string;
  updated_at: string;
}

export interface MasterKey {
  id: string;
  key_alias: string;
  key_version: number;
  key_hash_masked: string;
  algorithm: EncryptionAlgorithm;
  key_length_bits: number;
  status: MasterKeyStatus;
  activated_at: string | null;
  expires_at: string | null;
  rotated_at: string | null;
  created_by_email: string | null;
  created_at: string;
  has_recovery_codes: boolean;
  recovery_codes_count: number;
}

export interface RecoveryCode {
  id: string;
  code_hint: string | null;
  status: RecoveryCodeStatus;
  created_by_email: string | null;
  created_at: string;
  used_by_email: string | null;
  used_at: string | null;
  expires_at: string | null;
}

export interface ComplianceStatus {
  compliance_mode: ComplianceMode;
  is_compliant: boolean;
  issues: string[]; // JSON array from DB
  last_audit_date: string | null;
  policy_enforcement_mode: string;
  key_rotation_status: string;
  days_until_rotation_required: number | null;
}

export interface AuditLogEntry {
  id: string;
  tenant_id: string | null;
  event_type: string;
  event_category: string;
  event_severity: string;
  actor_id: string | null;
  actor_email: string | null;
  target_key_id: string | null;
  target_key_alias: string | null;
  details: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  request_id: string | null;
  requires_review: boolean;
  reviewed_at: string | null;
  reviewed_by: string | null;
  reviewed_by_email: string | null;
  review_notes: string | null;
  created_at: string;
}

// =============================================
// ENCRYPTION POLICY HOOKS
// Note: These hooks call RPC functions that need to be created via migration.
// The type casting bypasses TypeScript until the database types are regenerated.
// =============================================

export function useEncryptionPolicy(tenantId?: string | null) {
  return useQuery({
    queryKey: ["encryption-policy", tenantId],
    queryFn: async (): Promise<EncryptionPolicy | null> => {
      // Cast to any to bypass type checking - function may not exist yet
      const { data, error } = await (supabase.rpc as any)("calie_admin_get_encryption_policy", {
        p_tenant_id: tenantId ?? null,
      });

      if (error) {
        console.error("Error fetching encryption policy:", error);
        throw error;
      }

      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return result as EncryptionPolicy | null;
    },
  });
}

export interface UpsertPolicyParams {
  tenant_id?: string | null;
  approved_algorithms?: EncryptionAlgorithm[];
  default_algorithm?: EncryptionAlgorithm;
  min_key_length_bits?: number;
  max_key_length_bits?: number;
  require_strong_entropy?: boolean;
  rotation_period_days?: number;
  rotation_warning_days?: number;
  enforce_rotation?: boolean;
  enforcement_mode?: EnforcementMode;
  auto_rotate?: boolean;
  compliance_mode?: ComplianceMode;
  require_key_escrow?: boolean;
  require_dual_control?: boolean;
  require_audit_logging?: boolean;
  allow_key_export?: boolean;
  require_hsm?: boolean;
  key_derivation_function?: string;
  key_derivation_iterations?: number;
}

export function useUpsertEncryptionPolicy() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpsertPolicyParams): Promise<string> => {
      // Send all parameters including compliance-related ones
      const { data, error } = await (supabase.rpc as any)("calie_admin_upsert_encryption_policy", {
        p_tenant_id: params.tenant_id ?? null,
        p_approved_algorithms: params.approved_algorithms ?? ["AES-256-GCM"],
        p_default_algorithm: params.default_algorithm ?? "AES-256-GCM",
        p_min_key_length_bits: params.min_key_length_bits ?? 256,
        p_max_key_length_bits: params.max_key_length_bits ?? 4096,
        p_require_strong_entropy: params.require_strong_entropy ?? true,
        p_rotation_period_days: params.rotation_period_days ?? 90,
        p_rotation_warning_days: params.rotation_warning_days ?? 14,
        p_enforce_rotation: params.enforce_rotation ?? false,
        p_auto_rotate: params.auto_rotate ?? false,
        p_enforcement_mode: params.enforcement_mode ?? "warn",
        p_key_derivation_function: params.key_derivation_function ?? "PBKDF2",
        p_key_derivation_iterations: params.key_derivation_iterations ?? 100000,
        p_compliance_mode: params.compliance_mode ?? "none",
        p_require_key_escrow: params.require_key_escrow ?? false,
        p_require_dual_control: params.require_dual_control ?? false,
        p_require_audit_logging: params.require_audit_logging ?? true,
        p_allow_export: params.allow_key_export ?? false,
        p_require_hardware_security: params.require_hsm ?? false,
      });

      if (error) {
        console.error("Error upserting encryption policy:", error);
        throw error;
      }

      return data as string;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["encryption-policy", variables.tenant_id] });
      queryClient.invalidateQueries({ queryKey: ["compliance-status"] });
    },
  });
}

// =============================================
// MASTER KEY HOOKS
// =============================================

export function useMasterKeys() {
  return useQuery({
    queryKey: ["master-keys"],
    queryFn: async (): Promise<MasterKey[]> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_list_master_keys");

      if (error) {
        console.error("Error fetching master keys:", error);
        throw error;
      }

      return (data || []) as MasterKey[];
    },
  });
}

export interface CreateMasterKeyParams {
  key_alias: string;
  key_hash: string;
  algorithm?: EncryptionAlgorithm;
  key_length_bits?: number;
  expires_at?: string | null;
}

export function useCreateMasterKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateMasterKeyParams): Promise<string> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_create_master_key", {
        p_key_alias: params.key_alias,
        p_key_hash: params.key_hash,
        p_algorithm: params.algorithm ?? "AES-256-GCM",
        p_key_length_bits: params.key_length_bits ?? 256,
        p_expires_at: params.expires_at ?? null,
      });

      if (error) {
        console.error("Error creating master key:", error);
        throw error;
      }

      return data as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["master-keys"] });
      queryClient.invalidateQueries({ queryKey: ["encryption-audit-log"] });
    },
  });
}

export interface RevokeMasterKeyParams {
  key_id: string;
  reason?: string;
}

export function useRevokeMasterKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: RevokeMasterKeyParams): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_revoke_master_key", {
        p_key_id: params.key_id,
        p_reason: params.reason ?? null,
      });

      if (error) {
        console.error("Error revoking master key:", error);
        throw error;
      }

      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["master-keys"] });
      queryClient.invalidateQueries({ queryKey: ["encryption-audit-log"] });
    },
  });
}

// =============================================
// RECOVERY CODE HOOKS
// =============================================

export function useRecoveryCodes(masterKeyId: string) {
  return useQuery({
    queryKey: ["recovery-codes", masterKeyId],
    queryFn: async (): Promise<RecoveryCode[]> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_list_recovery_codes", {
        p_master_key_id: masterKeyId,
      });

      if (error) {
        console.error("Error fetching recovery codes:", error);
        throw error;
      }

      return (data || []) as RecoveryCode[];
    },
    enabled: !!masterKeyId,
  });
}

export interface CreateRecoveryCodeParams {
  master_key_id: string;
  code_hash: string;
  code_hint?: string;
  expires_at?: string | null;
}

export function useCreateRecoveryCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateRecoveryCodeParams): Promise<string> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_create_recovery_code", {
        p_master_key_id: params.master_key_id,
        p_code_hash: params.code_hash,
        p_code_hint: params.code_hint ?? null,
        p_expires_at: params.expires_at ?? null,
      });

      if (error) {
        console.error("Error creating recovery code:", error);
        throw error;
      }

      return data as string;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["recovery-codes", variables.master_key_id] });
      queryClient.invalidateQueries({ queryKey: ["master-keys"] });
      queryClient.invalidateQueries({ queryKey: ["encryption-audit-log"] });
    },
  });
}

export interface UseRecoveryCodeParams {
  master_key_id: string;
  code_hash: string;
}

export function useUseRecoveryCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UseRecoveryCodeParams): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_use_recovery_code", {
        p_master_key_id: params.master_key_id,
        p_code_hash: params.code_hash,
      });

      if (error) {
        console.error("Error using recovery code:", error);
        throw error;
      }

      return data as boolean;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["recovery-codes", variables.master_key_id] });
      queryClient.invalidateQueries({ queryKey: ["master-keys"] });
      queryClient.invalidateQueries({ queryKey: ["encryption-audit-log"] });
    },
  });
}

// =============================================
// COMPLIANCE HOOKS
// =============================================

export function useComplianceStatus(tenantId?: string | null) {
  return useQuery({
    queryKey: ["compliance-status", tenantId],
    queryFn: async (): Promise<ComplianceStatus | null> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_get_compliance_status", {
        p_tenant_id: tenantId ?? null,
      });

      if (error) {
        console.error("Error fetching compliance status:", error);
        throw error;
      }

      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return result as ComplianceStatus | null;
    },
  });
}

export interface AuditLogParams {
  tenant_id?: string | null;
  limit?: number;
  offset?: number;
  event_type?: string | null;
  requires_review?: boolean | null;
}

export function useEncryptionAuditLog(params: AuditLogParams = {}) {
  return useQuery({
    queryKey: ["encryption-audit-log", params],
    queryFn: async (): Promise<AuditLogEntry[]> => {
      // Use calie_admin_get_audit_log (the actual function name)
      const { data, error } = await (supabase.rpc as any)("calie_admin_get_audit_log", {
        p_tenant_id: params.tenant_id ?? null,
        p_limit: params.limit ?? 100,
        p_offset: params.offset ?? 0,
      });

      if (error) {
        console.error("Error fetching encryption audit log:", error);
        throw error;
      }

      return (data || []) as AuditLogEntry[];
    },
  });
}

export function useReviewAuditEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (auditId: string): Promise<boolean> => {
      const { data, error } = await (supabase.rpc as any)("calie_admin_review_audit_entry", {
        p_audit_id: auditId,
      });

      if (error) {
        console.error("Error reviewing audit entry:", error);
        throw error;
      }

      return data as boolean;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["encryption-audit-log"] });
    },
  });
}

// =============================================
// ENCRYPTION ENFORCEMENT CHECK
// =============================================

export interface EncryptionAllowedStatus {
  is_allowed: boolean;
  block_reason: string | null;
  days_overdue: number | null;
  rotation_period: number | null;
  enforcement_mode: EnforcementMode | null;
  warning_message: string | null;
}

export function useEncryptionAllowedStatus(tenantId?: string | null) {
  return useQuery({
    queryKey: ["encryption-allowed", tenantId],
    queryFn: async (): Promise<EncryptionAllowedStatus | null> => {
      // Cast to any to bypass type checking - function may not exist yet
      const { data, error } = await (supabase.rpc as any)("calie_check_encryption_allowed", {
        p_tenant_id: tenantId ?? null,
      });

      if (error) {
        console.error("Error checking encryption allowed:", error);
        throw error;
      }

      const result = Array.isArray(data) && data.length > 0 ? data[0] : null;
      return result as EncryptionAllowedStatus | null;
    },
  });
}

// =============================================
// UTILITY FUNCTIONS
// =============================================

export const ALGORITHM_LABELS: Record<EncryptionAlgorithm, string> = {
  "AES-256-GCM": "AES-256-GCM (Recommended)",
  "AES-256-CBC": "AES-256-CBC",
  "AES-128-GCM": "AES-128-GCM",
  "ChaCha20-Poly1305": "ChaCha20-Poly1305",
};

export const COMPLIANCE_LABELS: Record<ComplianceMode, string> = {
  none: "No Compliance",
  hipaa: "HIPAA",
  soc2: "SOC 2",
  pci_dss: "PCI-DSS",
  gdpr: "GDPR",
  hipaa_soc2: "HIPAA + SOC 2",
};

export const COMPLIANCE_DESCRIPTIONS: Record<ComplianceMode, string> = {
  none: "No specific compliance requirements",
  hipaa: "Health Insurance Portability and Accountability Act compliance for healthcare data",
  soc2: "Service Organization Control 2 for security and availability",
  pci_dss: "Payment Card Industry Data Security Standard for payment data",
  gdpr: "General Data Protection Regulation for EU data protection",
  hipaa_soc2: "Combined HIPAA and SOC 2 compliance requirements",
};

export const ROTATION_PRESETS = [
  { label: "30 days", value: 30 },
  { label: "60 days", value: 60 },
  { label: "90 days (Recommended)", value: 90 },
  { label: "180 days", value: 180 },
  { label: "365 days", value: 365 },
];

export const ENFORCEMENT_MODE_LABELS: Record<EnforcementMode, string> = {
  warn: "Warn Only",
  block: "Block New Encryption",
  strict: "Strict (Block + Alert)",
};

export const ENFORCEMENT_MODE_DESCRIPTIONS: Record<EnforcementMode, string> = {
  warn: "Log a warning but allow encryption operations to continue",
  block: "Block encryption operations until key is rotated",
  strict: "Block operations and create a critical alert requiring review",
};
